import { geometryProfileIdForAsset, Orientation, PlaybackMode, preparationPlan, viewportFor } from "./authoring.js";

// Preview intent is per playlist occurrence. The library asset and its authored
// hotspot coordinate plane remain unchanged until an explicit editing operation.
export function itemPreviewPlan(item, asset) {
  const profileId = geometryProfileIdForAsset(asset);
  const orientation = Number(asset.orientation ?? asset.compatibleOrientation ?? Orientation.landscape);
  const width = Number(asset.sourceWidth ?? asset.width), height = Number(asset.sourceHeight ?? asset.height);
  const requested = Number(item.requestedPresentation ?? (Number(asset.playbackMode) === PlaybackMode.static ? 1 : 3));
  const presentation = requested === 3 ? "scroll" : "fit";
  let plan;
  try { plan = preparationPlan(width, height, presentation, orientation, profileId); }
  catch (error) {
    if (presentation !== "scroll" || !/already matches|same aspect ratio/i.test(error.message)) {
      error.customerSafe = true; throw error;
    }
    plan = preparationPlan(width, height, "fit", orientation, profileId);
  }
  return { ...plan, presentation: plan.playbackMode === PlaybackMode.static ? "fit" : "scroll", requested,
    orientation, profileId, viewport: viewportFor(orientation, profileId), sourceWidth: width, sourceHeight: height,
    signature: JSON.stringify([asset.assetId ?? asset.id, asset.logicalSourceAssetId, asset.sourceSha256 ?? asset.sha256,
      width, height, asset.width, asset.height, asset.preparation, requested, orientation, profileId]),
  };
}

const sameRequest = (left, right) => left && right &&
  left.account === right.account && left.workspace === right.workspace && left.item === right.item &&
  left.plan.signature === right.plan.signature;

// One selected image only; neither a same-asset duplicate nor a late response
// may reuse another item's presentation or replace the current preview.
export class SelectedItemPreview {
  positions = new WeakMap();
  clear() { this.current = null; this.positions = new WeakMap(); }
  retry() { this.current = null; }
  rememberPosition(item, signature, kind, left, top) {
    const positions = this.positions.get(item) ?? new Map();
    positions.set(signature + ":" + kind, { left: Math.max(0, left), top: Math.max(0, top) });
    this.positions.set(item, positions);
  }
  position(item, signature, kind) { return this.positions.get(item)?.get(signature + ":" + kind) ?? { left: 0, top: 0 }; }
  peek(request) { return sameRequest(this.current, request) ? this.current.result : null; }
  async load(request, produce) {
    if (sameRequest(this.current, request)) return this.current.pending;
    const job = { ...request, result: null };
    this.current = job;
    job.pending = (async () => {
      try {
        const result = await produce(request.plan);
        if (this.current !== job) return null;
        if (!result?.url || result.width !== request.plan.outputWidth || result.height !== request.plan.outputHeight)
          throw new Error("The prepared preview does not match this artwork's selected layout.");
        job.result = result;
        return result;
      } catch (error) {
        if (this.current !== job) return null;
        job.result = { error };
        return job.result;
      }
    })();
    return job.pending;
  }
}

export function applyItemPreviewGeometry(root, plan) {
  const viewport = root.querySelector("[data-item-preview-viewport]");
  const image = root.querySelector("[data-item-preview-artwork]");
  if (!viewport || !image) return;
  image.style.width = `${plan.outputWidth}px`;
  image.style.height = `${plan.outputHeight}px`;
  viewport.style.width = `${plan.viewport.width}px`;
  viewport.style.height = `${plan.viewport.height}px`;
  // Keep the advertised visible pixel area even with classic scrollbars.
  for (let pass = 0; pass < 3; pass++) {
    const horizontal = Math.max(0, viewport.offsetWidth - viewport.clientWidth);
    const vertical = Math.max(0, viewport.offsetHeight - viewport.clientHeight);
    viewport.style.width = `${plan.viewport.width + horizontal}px`;
    viewport.style.height = `${plan.viewport.height + vertical}px`;
  }
}
